import React, { useState, useCallback } from 'react';
import { Map, Marker, NavigationControl, ScaleControl } from 'react-map-gl/mapbox';
import 'mapbox-gl/dist/mapbox-gl.css';

interface GeographicMapProps {
  clusters?: Array<{
    id: string;
    name: string;
    center: [number, number]; // [longitude, latitude]
    practiceCount: number;
  }>;
  onClusterClick?: (clusterId: string) => void;
  className?: string;
}

// Initial view centered on Southeast US
const INITIAL_VIEW_STATE = {
  longitude: -82.0,
  latitude: 33.5,
  zoom: 5.5,
  pitch: 0,
  bearing: 0,
};

// Map style - clean, minimal light theme
const MAP_STYLE = 'mapbox://styles/mapbox/light-v11';

/**
 * GeographicMap Component
 *
 * Interactive map showing medical practice clusters across the Southeast US
 * Uses Mapbox GL JS for smooth, performant rendering
 */
export const GeographicMap: React.FC<GeographicMapProps> = ({
  clusters = [],
  onClusterClick,
  className = '',
}) => {
  const [viewState, setViewState] = useState(INITIAL_VIEW_STATE);
  const [hoveredCluster, setHoveredCluster] = useState<string | null>(null);

  const mapboxToken = process.env.REACT_APP_MAPBOX_TOKEN;

  // Handle cluster marker click
  const handleClusterClick = useCallback((clusterId: string, center: [number, number]) => {
    // Zoom to cluster
    setViewState({
      ...viewState,
      longitude: center[0],
      latitude: center[1],
      zoom: 10,
      pitch: 0,
      bearing: 0,
    });

    if (onClusterClick) {
      onClusterClick(clusterId);
    }
  }, [viewState, onClusterClick]);

  // Show placeholder if no token
  if (!mapboxToken) {
    return (
      <div className={`w-full h-full flex items-center justify-center bg-charcoal-gray bg-opacity-5 ${className}`}>
        <div className="text-center p-8 max-w-md">
          <h3 className="text-xl font-bold text-navy-blue mb-2">
            Mapbox Configuration Required
          </h3>
          <p className="text-medium-gray mb-4">
            To view the interactive map, please configure your Mapbox API token.
          </p>
          <div className="text-sm text-left bg-white p-4 rounded border border-light-gray">
            <p className="font-mono text-xs mb-2">
              1. Sign up at <a href="https://mapbox.com" target="_blank" rel="noopener noreferrer" className="text-light-blue underline">mapbox.com</a>
            </p>
            <p className="font-mono text-xs mb-2">
              2. Create a <code className="bg-light-gray px-1">.env</code> file
            </p>
            <p className="font-mono text-xs">
              3. Add: <code className="bg-light-gray px-1">REACT_APP_MAPBOX_TOKEN=your_token</code>
            </p>
          </div>
          <p className="text-xs text-medium-gray mt-4">
            See <code>MAPBOX_SETUP.md</code> for detailed instructions
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`w-full h-full relative ${className}`}>
      <Map
        {...viewState}
        onMove={(evt: any) => setViewState(evt.viewState)}
        mapStyle={MAP_STYLE}
        mapboxAccessToken={mapboxToken}
        style={{ width: '100%', height: '100%' }}
        attributionControl={false}
      >
        {/* Cluster Markers */}
        {clusters.map((cluster) => (
          <Marker
            key={cluster.id}
            longitude={cluster.center[0]}
            latitude={cluster.center[1]}
            anchor="center"
          >
            <div
              className="cursor-pointer transform transition-all duration-200"
              style={{
                transform: hoveredCluster === cluster.id ? 'scale(1.2)' : 'scale(1)',
              }}
              onMouseEnter={() => setHoveredCluster(cluster.id)}
              onMouseLeave={() => setHoveredCluster(null)}
              onClick={() => handleClusterClick(cluster.id, cluster.center)}
            >
              {/* Cluster Circle */}
              <div
                className={`
                  w-12 h-12 rounded-full
                  bg-forest-green
                  flex items-center justify-center
                  text-white font-bold text-sm
                  shadow-lg
                  border-2
                  ${hoveredCluster === cluster.id ? 'border-light-blue' : 'border-white'}
                `}
              >
                {cluster.practiceCount}
              </div>

              {/* Cluster Label */}
              <div className="absolute top-14 left-1/2 transform -translate-x-1/2 whitespace-nowrap">
                <div className="bg-white px-2 py-1 rounded shadow-md border border-light-gray text-xs font-semibold text-navy-blue">
                  {cluster.name}
                </div>
              </div>
            </div>
          </Marker>
        ))}

        {/* Sightline Capital Marker - McKinney, TX */}
        <Marker
          longitude={-96.6153}
          latitude={33.1976}
          anchor="center"
        >
          <div>
            {/* Sightline Logo Circle */}
            <div
              className="w-12 h-12 rounded-full bg-white flex items-center justify-center shadow-lg border-2"
              style={{ borderColor: '#990000' }}
            >
              <img
                src="/sightline_capital_logo_navyblue.svg"
                alt="Sightline Capital"
                className="w-11 h-11"
              />
            </div>

            {/* Sightline Label */}
            <div className="absolute top-14 left-1/2 transform -translate-x-1/2 whitespace-nowrap">
              <div className="bg-white px-2 py-1 rounded shadow-md border border-light-gray text-xs font-semibold text-navy-blue">
                Sightline Capital
              </div>
            </div>
          </div>
        </Marker>

        {/* Navigation Controls */}
        <NavigationControl position="top-right" />

        {/* Scale Control */}
        <ScaleControl position="bottom-right" unit="imperial" />
      </Map>

      {/* Legend */}
      <div className="absolute bottom-4 left-4 bg-white p-3 rounded-lg shadow-lg border border-light-gray">
        <h4 className="text-xs font-bold text-navy-blue mb-2">Micro-Regional Clusters</h4>
        <div className="flex items-center gap-2 text-xs text-medium-gray">
          <div className="w-6 h-6 rounded-full bg-forest-green flex items-center justify-center text-white text-[10px] font-bold">
            #
          </div>
          <span>Practice Count</span>
        </div>
        <p className="text-[10px] text-medium-gray mt-2">Click cluster to zoom in</p>
      </div>

      {/* Reset View Button — only show when zoomed in */}
      {viewState.zoom > 6 && (
        <button
          className="absolute top-4 left-4 bg-white text-navy-blue font-bold text-xs px-3 py-2 rounded-lg shadow-lg border border-light-gray hover:bg-gray-50 transition-all"
          onClick={() => setViewState(INITIAL_VIEW_STATE)}
        >
          Reset View
        </button>
      )}
    </div>
  );
};

export default GeographicMap;
